import { useCallback, useMemo } from 'react';
import { Modal } from '@betty/navigation';
import { Config } from '@betty/toolkit/config';
import { logError } from '@betty/toolkit/logging';
import { useSelector } from '@betty/toolkit/state';
import { selectAccount } from '@betty/account/state';
import { loadOnfido } from '../utils/onfido';
import KYCService from '../services/KYCService/KYCService';

const useOnfido = () => {
  const account = useSelector(selectAccount);
  const { phoneNumber } = account?.personalDetails || {};

  const fetchConfig = useCallback(
    () =>
      KYCService.initOnfido({
        data: {
          referrer: __DEV__
            ? window.origin + '/'
            : Config.PLATFORM_URL.endsWith('/')
              ? Config.PLATFORM_URL
              : Config.PLATFORM_URL + '/',
        },
      }),
    [],
  );

  const start = useCallback(({ onInit }: { onInit?: () => void } = {}) => {
    onInit?.();

    Modal.open({
      namespace: 'onboarding',
      type: 'Onfido',
      options: {
        hideHeader: true,
      },
    });
  }, []);

  const init = useCallback(
    async ({ onComplete }: { onComplete?: () => void }) => {
      try {
        const [Onfido, { workflowRunId, sessionToken }] = await Promise.all([
          loadOnfido(),
          fetchConfig(),
        ]);

        return Onfido.init({
          token: sessionToken,
          workflowRunId: workflowRunId,
          containerId: 'onfido-container',
          userDetails: {
            smsNumber: phoneNumber,
          },
          smsNumberCountryCode: 'CA',
          language: 'en_US',
          customUI: {
            colorBackgroundSurfaceModal: 'transparent',
            colorBorderSurfaceModal: 'transparent',

            borderRadiusButton: 'var(--radius-200)',
            borderRadiusSurfaceModal: 'var(--radius-400)',

            fontFamilyTitle: 'DMSerifText',
            fontFamilySubtitle: 'DMSerifText',
            fontFamilyBody: 'Inter-Regular',
            fontSizeTitle: 'var(--text-800)',
            fontSizeSubtitle: 'var(--text-400)',
            fontSizeBody: 'var(--text-200)',

            colorContentTitle: 'var(--darkSurfaceAccentSolid)',
            colorContentSubtitle: 'var(--darkSurfaceTertiary)',
            colorContentBody: 'var(--darkSurfaceTertiary)',

            colorContentButtonPrimaryText: 'var(--accentSurfaceSecondary)',
            colorBackgroundButtonPrimary: 'var(--darkSurfaceAccentSolid)',
            colorBorderButtonPrimary: 'var(--darkSurfaceAccentSolid)',
            colorBackgroundButtonPrimaryHover: 'var(--accentSolidBackground)',
            colorBackgroundButtonPrimaryActive: 'var(--accentSolidBackground)',
            colorBorderButtonPrimaryHover: 'var(--accentSolidBackground)',
            colorBorderButtonPrimaryActive: 'var(--accentSolidBackground)',

            colorContentButtonSecondaryText: 'var(--darkSurfaceAccentSolid)',
            colorBackgroundButtonSecondary: 'var(--screenBackground)',
            colorBorderButtonSecondary: 'var(--darkSurfaceAccentSolid)',
            colorBackgroundButtonSecondaryHover:
              'var(--darkSurfaceAccentDisabled)',
            colorBackgroundButtonSecondaryActive:
              'var(--darkSurfaceAccentDisabled)',
            colorBorderButtonSecondaryHover: 'var(--borderAccentDisabled)',
            colorBorderButtonSecondaryActive: 'var(--borderAccentDisabled)',

            colorContentDocTypeButton: 'var(--darkSurfaceAccentSolid)',
            colorBackgroundDocTypeButton: 'var(--screenBackground)',
            colorBorderDocTypeButton: 'var(--darkSurfaceAccentSolid)',
            colorBorderDocTypeButtonHover: 'var(--accentSolidBackground)',
            colorBorderDocTypeButtonActive: 'var(--accentSolidBackground)',

            colorIcon: 'var(--darkSurfaceAccentSolid)',
            colorInputOutline: 'var(--darkSurfaceAccentSolid)',
            colorBackgroundIcon: 'var(--darkSurfaceAccentSolid)',

            colorBackgroundSelector: 'var(--screenBackground)',
            colorBackgroundButtonIconActive: 'var(--accentSolidBackground)',
            colorBackgroundButtonIconHover: 'var(--accentSolidBackground)',

            colorContentLinkTextHover: 'var(--darkSurfaceTertiary)',
            colorBorderLinkUnderline: 'var(--darkSurfaceTertiary)',
            colorBackgroundLinkHover: 'transparent',
            colorBackgroundLinkActive: 'transparent',

            colorBackgroundInfoPill: 'var(--darkSurfaceAccentSolid)',
            colorContentInfoPill: 'var(--accentSurfaceSecondary)',

            colorBackgroundAlertInfo: 'var(--darkSurfaceAccentSolid)',
            colorBackgroundAlertInfoLinkActive: 'transparent',
            colorBackgroundAlertInfoLinkHover: 'transparent',
            colorContentAlertInfo: 'var(--accentSurfaceSecondary)',
          },
          onComplete: () => {
            KYCService.processOnfido();

            onComplete?.();
          },
          onError: (error) => {
            logError('Onfido Verification Error', error);
          },
        });
      } catch (error) {
        if (error instanceof Error) {
          logError('Onfido Initializing Error', error);
        }
      }
    },
    [phoneNumber, fetchConfig],
  );

  return useMemo(() => ({ start, init }), [start, init]);
};

export default useOnfido;
